export const meta = {
  title: "The Ultimate Switzerland Packing Guide (All Seasons)",
  description: "What to pack for your Swiss Alps vacation? A complete checklist for summer hiking, winter skiing, and spa relaxation in Beatenberg.",
  date: "2024-03-10",
  image: "/images/local_attractions/alpine_hiking_trails.webp",
  author: "Beatenberg Concierge",
  category: "Travel Planning",
  keywords: ["packing list switzerland", "what to wear in swiss alps summer", "winter packing list switzerland", "switzerland travel checklist"],
  faqs: [
    {
      question: "Do I need dress shoes for dinner?",
      answer: "At Restaurant Jungfrau, we have a 'smart casual' dress code. Hiking boots are fine for lunch on the terrace, but we appreciate cleaner footwear for dinner. A simple pair of leather shoes or nice sneakers works perfectly."
    },
    {
      question: "Do you provide bathrobes?",
      answer: "Yes! All our rooms are equipped with fluffy bathrobes and slippers for your spa visits."
    },
    {
      question: "Can I rent hiking poles?",
      answer: "Yes, we have high-quality hiking poles available for rent at the reception for free."
    },
    {
      question: "Is there a laundry service?",
      answer: "We offer an express laundry service for guests. If you've been hiking for a week, we can get your gear fresh again overnight."
    }
  ]
}

# The Ultimate Switzerland Packing Guide

Packing for the Swiss Alps can be tricky. The weather in the mountains can change in minutes, and you might find yourself hiking a glacier in the morning and dining in a 4-star restaurant in the evening.

<JournalQuote>
  "There is no such thing as bad weather, only bad clothing." — Swiss Proverb
</JournalQuote>

## The Golden Rule: Layering
The secret to comfort in the Alps is the "onion principle" (Zwiebelprinzip). Instead of one thick coat, wear multiple thin layers that you can add or remove as needed.

<JournalList items={[
  "Base Layer: Merino wool or synthetic thermal (wicks moisture away)",
  "Mid Layer: Fleece or wool sweater (insulation)",
  "Outer Layer: Waterproof and windproof shell (protection)"
]} />

## Summer Packing Checklist (June - September)
Summer in Beatenberg brings warm days (20-25°C) but cool nights.

<JournalList items={[
  "Sturdy hiking boots (broken in!)",
  "Lightweight rain jacket",
  "Sunglasses and high-SPF sunscreen (the sun is stronger at altitude)",
  "Swimwear (for Lake Thun and our spa)",
  "Refillable water bottle",
  "Daypack (20-30 liters)",
  "Smart casual evening wear for dinner"
]} />

## Winter Packing Checklist (December - March)
For skiing, winter hiking, or just enjoying the snow.

<JournalList items={[
  "Warm winter coat (down or synthetic fill)",
  "Waterproof trousers",
  "Thermal underwear (top and bottom)",
  "Warm hat (beanie) and gloves",
  "Scarf or neck gaiter",
  "Winter boots with good grip",
  "Wool socks (bring extras!)"
]} />

<JournalImage 
  src="/images/Views/a_view_of_a_mountain_range_with_snow_covered_mount_94.jpg" 
  alt="Winter clothing in the snow"
  caption="Proper gear makes winter activities a joy, not a struggle."
/>

## Spa & Resort Essentials
Don't forget the items you'll need for relaxing at Beatenberg Resort & Spa.

<JournalList items={[
  "Swimsuit (two is better so one can dry)",
  "Flip-flops or pool slides",
  "Books or E-reader",
  "Power adapter (Switzerland uses Type J plugs)",
  "Camera with extra memory cards"
]} />

<JournalAccordion items={[
  {
    question: "Do I need dress shoes for dinner?",
    answer: "At Restaurant Jungfrau, we have a 'smart casual' dress code. Hiking boots are fine for lunch on the terrace, but we appreciate cleaner footwear for dinner. A simple pair of leather shoes or nice sneakers works perfectly."
  },
  {
    question: "Do you provide bathrobes?",
    answer: "Yes! All our rooms are equipped with fluffy bathrobes and slippers for your spa visits."
  },
  {
    question: "Can I rent hiking poles?",
    answer: "Yes, we have high-quality hiking poles available for rent at the reception for free."
  },
  {
    question: "Is there a laundry service?",
    answer: "We offer an express laundry service for guests. If you've been hiking for a week, we can get your gear fresh again overnight."
  }
]} />

<JournalCTA href="/book">Book Your Stay</JournalCTA>
